import React, { useEffect, useState } from "react";
import ScrollToTop from "react-scroll-to-top";
import { useTheme } from '@mui/material/styles';
import {
  Button,
  Container,
  Grid,
  Typography,
  Tooltip,
  Modal,
  Box,
  Divider,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ConfirmModal from '../../components/layout/ConfirmModal';
import { getBookingFormData } from "../../redux/actions/book";
import { customizePerkPage } from "../../redux/actions/perk";
import config from "../../config/config";
import { resizePerkHeaderImage } from "../../utils/resizeFile";

import IMG_PERKS_OVERVIEW from "../../assets/img/perks-overview.jpg";
import IMG_HORIZONTAL_PLACEHOLDER from "../../assets/img/horizontal-placeholder.jpg";

const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const CustomizePerkForm = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => ({
    user: state.auth.user
  }));
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    dispatch(getBookingFormData(user?.bookFormId));
  }, []);

  const emailNotification = useSelector(state => state.book.emailNotification);

  const [heading, setHeading] = useState('');
  const [message, setMessage] = useState('');
  const [valueLink, setValueLink] = useState("");
  const [openLinkCopyTooltip, setOpenLinkCopyTooltip] = useState(false);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImageSelected, setFeatureImageSelected] = useState(null);

  useEffect(() => {
    if (user?.firstName) {
      setValueLink(`${config.CLIENT_BASE_URL}/${user?.username}/perks`);
    }
  }, [user]);

  useEffect(() => {
    if(emailNotification) {
      setHeading(emailNotification.perkHeading);
      setMessage(emailNotification.perkMessage);
      setFeatureImageSelected(emailNotification.perkFeaturedImage);
    }
  }, [emailNotification]);

  const handleTooltipClose = () => {
    setOpenLinkCopyTooltip(false);
  };

  const handleCopyLink = () => {
    copyToClipboard(valueLink);
    setOpenLinkCopyTooltip(true);
  }

  async function copyToClipboard(textToCopy) {
    // Navigator clipboard api needs a secure context (https)
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
    } else {
      // Use the 'out of viewport hidden text area' trick
      const textArea = document.createElement("textarea");
      textArea.value = textToCopy;
          
      // Move textarea out of the viewport so it's not visible
      textArea.style.position = "absolute";
      textArea.style.left = "-999999px";
          
      document.body.prepend(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
      } catch (error) {
        console.error(error);
      } finally {
        textArea.remove();
      }
    };
  }

  const handleFeaturedImageUpload = () => {
    document.querySelector('#Featured-Image-Selection').value = null;
    document.querySelector('#Featured-Image-Selection').click();
  }
  const handleFeaturedImageRemove = () => {
    setFeaturedImage(null);
    setFeatureImageSelected(null);
  }

  const handleSelectFeaturedImage = async e => {
    const files = e.target.files;
    if (files.length === 0) {
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type)) {
      return;
    }
    if (files[0].size > MAX_FILE_SIZE) {
      return;
    }
    const file = files[0];
    const resizedFile = await resizePerkHeaderImage(file, file.type.split('/').pop().toUpperCase());
    setFeaturedImage(resizedFile);
    const reader = new FileReader();
    reader.readAsDataURL(resizedFile);
    reader.onloadend = (e) => {
      setFeatureImageSelected(reader.result);
    };
  }

  const handleOpen = () => {
    setShowConfirmModal(true);
  }

  const handleClose = () => setOpen(false);
  const onCloseConfirmModal = () => setShowConfirmModal(false);

  const onConfirmYes = async () => {
    onCloseConfirmModal();
    let formData = new FormData();
    formData.append('bookFormId', emailNotification.bookFormId);
    formData.append('perkHeading', heading);
    formData.append('perkMessage', message);
    formData.append('perkFeaturedImage', featuredImage);
    await dispatch(customizePerkPage(formData));
    setOpen(true);
  }

  return (
    <Container maxWidth="lg">
      <Grid
        item
        className="page-title"
        container
        justifyContent='space-between'
        sx={{ my: { sm: 2, md: 5 }, marginInline: 'auto' }}
      >
        <Grid item xs={12} sm={6}>
          <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600 }}>Perks Page Settings</Typography>
          <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 500, mt: '3px', opacity: 0.75 }}>Customize your Perks page with personalized messaging and embed in your marketing.</Typography>
        </Grid>
        <Grid item container direction='row' justifyContent='flex-end' alignItems="center" xs={12} sm="auto" sx={{ my: 1 }}>
          <Button
            color="primary"
            variant="contained"
            sx={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', color: '#fff', padding: '10px 30px' }}
            onClick={handleOpen}
          >
            Save Settings
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        display='flex'
        direction='row'
        alignItems='flex-start'
        spacing={3}
      >
        <Grid item container lg={8}>
          <Container
            direction='column'
            sx={{ backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '40px !important' }}
          >
            <Grid>
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: theme.palette.mode ==="light" ? "#3f4254" : "#fff" }}>Welcome Message</Typography>
              <Typography sx={{ fontSize: '11px', mt: 0.5, fontWeight: 400, color: theme.palette.mode ==="light" ? "#a1a5b7" : "#565674" }}>This is a personalized greeting for clients when they launch your Perks page.</Typography>
            </Grid>
            <Divider sx={{ mt: 2 }} />
            <Grid container alignItems="center" sx={{ mt: 2 }}>
              <Grid item xs={12} md="auto">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, width: 200, color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>Heading</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="standard"
                  fullWidth
                  sx={{ mt: { xs: 1 } }}
                  value={heading}
                  onChange={e => setHeading(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      padding: '8px 20px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    },
                  }}
                />
              </Grid>
            </Grid>
            <Grid container alignItems="center" sx={{ mt: 2 }}>
              <Grid item xs={12} md="auto">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, width: 200, color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>Message</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="standard"
                  fullWidth
                  sx={{ mt: { xs: 1 } }}
                  multiline
                  rows={5}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      padding: '15px 20px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Container>
          <Container
            direction='column'
            sx={{ backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', padding: '40px !important' }}
          >
            <Grid>
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: theme.palette.mode ==="light" ? "#3f4254" : "#fff" }}>Featured Image</Typography>
              <Typography sx={{ fontSize: '11px', mt: 0.5, fontWeight: 400, color: theme.palette.mode ==="light" ? "#a1a5b7" : "#565674" }}>Customize your Perks page further by using a favorite image as the header background.</Typography>
            </Grid>
            <Divider sx={{ mt: 2 }} />
            <Grid container sx={{ mt: 2 }}>
              <Grid item xs={12} md="auto">
                <Typography sx={{ fontSize: '13px', fontWeight: 500, width: 200, color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>Image Upload</Typography>
              </Grid>
              <Grid item xs>
                <Box display="inline-flex" borderRadius="6px" position="relative" sx={{ boxShadow: 3 }}>
                  {featuredImageSelected
                    ? featuredImageSelected.includes('upload')
                      ? <Box
                          component="img"
                          src={`${config.SERVER_BASE_URL}/${featuredImageSelected}`}
                          sx={{ width: { xs: '100%', md: '350px' }, border: '3px solid white', borderRadius: '6px', mt: { xs: 1 } }}
                          alt="perk_featured_image"
                        />
                      : <Box
                          component="img"
                          src={featuredImageSelected}
                          sx={{ width: { xs: '100%', md: '350px' }, border: '3px solid white', borderRadius: '6px', mt: { xs: 1 } }}
                          alt="perk_featured_image"
                        />
                    : <Box
                        component="img"
                        src={IMG_HORIZONTAL_PLACEHOLDER}
                        sx={{ width: { xs: '100%', md: '350px' }, border: '3px solid white', borderRadius: '6px', mt: { xs: 1 } }}
                        alt="placeholder_image"
                      />
                  }
                  <span className="Image-upload" onClick={handleFeaturedImageUpload}>
                    <i className="fa fa-pen"></i>
                  </span>
                  <span className="Image-remove" onClick={handleFeaturedImageRemove}>
                    <i className="fa fa-remove"></i>
                  </span>
                </Box>
                <Typography sx={{ fontSize: '0.7rem', mt: 0.5, color: '#a1a5b7', fontWeight: 500 }}>
                  Recommended size 1920x480px. Allowed file types: png, jpg.
                </Typography>
                <input type="file" name="idCard" id="Featured-Image-Selection" style={{ visibility: 'hidden' }} onChange={handleSelectFeaturedImage} />
              </Grid>
            </Grid>
          </Container>
        </Grid>
        <Grid item container lg={4}>
          <Container
            direction='column'
            sx={{ backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d', borderRadius: 2, padding: '40px !important' }}>
            <Grid sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: '1.2', color: theme.palette.mode === 'light' ? '#3f4254' : '#cdcdde' }}>Displaying your Perks Page</Typography>
              <Divider sx={{ mt: 2, mb: 2 }} />
              <Typography sx={{ fontSize: '0.8rem', mt: 0.5, fontWeight: 400 }}>
                <p>A well-structured loyalty rewards campaign can supercharge your revenue with both repeat bookings and new bookings. Clientl helps you achieve this with our Perks functionality.</p>
                <p>Get started with our Perks templates, or create your own campaigns.</p>
                <Box component="img" src={IMG_PERKS_OVERVIEW} sx={{ width: '100%', objectFit: 'contain', mt: 0 }} alt="Perks Overview" />
			    </Typography>
              <Grid sx={{ mt: 4.5, mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: '1.2' }}>Direct Link</Typography>
                <Divider sx={{ mt: 2, mb: 1.5 }} />
                <Typography sx={{ fontSize: '0.8rem', mt: 0.5, color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>
                  Use this link to feature your Perks page on your website, ads or social media. (Or keep it exclusively for your inner circle only!)
                </Typography>
              </Grid>
              <TextField
                variant="standard"
                fullWidth
                value={valueLink}
                InputProps={{
                  disableUnderline: true,
                  style: {
                    padding: '8px 20px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 400,
                  },
                }}
                sx={{ mt: 1 }}
              />
              <Tooltip
                open={openLinkCopyTooltip}
                onClose={handleTooltipClose}
                title={'Link copied!'}
                leaveDelay={500}
                placement='top'
                arrow
                enterTouchDelay={0}
              >
                <Button
                  onClick={handleCopyLink}
                  sx={{
                    mt: 1,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    minWidth: '7rem',
                    backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2B2B40',
                    color: theme.palette.mode === 'dark' && '#6D6D80',
                    padding: '11px 20px',
                    '&:hover': {
                      color: theme.palette.mode === 'dark' && '#009EF7',
                    }
                  }}
                >
                  Copy Code
                </Button>
              </Tooltip>
            </Grid>
          </Container>
        </Grid>
      </Grid>
      <ScrollToTop
        smooth
        color="#009EF7"
      />
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 360,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            padding: '48px 32px 42px',
          }}
        >
          <Grid sx={{ width: '80%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#50CD89" }}>
              <Grid className="swal-x-content" sx={{ fontSize: '3em', color: '#50CD89' }} >&#10004;</Grid>
            </Grid>
            <Typography sx={{ fontSize: '2rem', textAlign: 'center', fontWeight: 700, mt: 2, mb: 1.5 }}>
              Success!
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', textAlign: 'center', fontWeight: 400 }}>
              Your settings have been saved.
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button color="success" variant="contained" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'none', color: '#fff', padding: '12px 30px' }} onClick={handleClose}>Continue</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <ConfirmModal
        visible={showConfirmModal}
        symbol="?"
        title="Save changes?"
		description="Your Perks page will be updated with these settings."
        titleYes="Confirm"
        titleNo="Cancel"
        onClickYes={onConfirmYes}
        onClickNo={onCloseConfirmModal}
      />
    </Container>
  );
}

export default CustomizePerkForm;
